import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ConnectionType } from '@/lib/api/types.gen';
import { useCreateBridge, useAssignSitesToRoute } from '@/lib/api/edgelink';
import { buttonVariants } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ArrowLeft, Cloud, Shield, Network, Loader2, Lock, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import SiteMultiSelect from './SiteMultiSelect';
import { CidrInput, validateCidr } from '@/components/ui/cidr-input';

type BridgeType = 'cloudflared' | 'ipsec';

const createBridgeSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name must be less than 100 characters'),
  initialRouteCidr: z.string().optional().refine(
    (val) => !val || validateCidr(val).isValid,
    (val) => ({ message: validateCidr(val ?? '').errorMessage || 'Invalid CIDR' })
  ),
});

type CreateBridgeFormValues = z.infer<typeof createBridgeSchema>;

export default function AddEdgeLinkPage() {
  const navigate = useNavigate();
  const [selectedType, setSelectedType] = useState<BridgeType>('cloudflared');
  const [selectedSites, setSelectedSites] = useState<string[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  const createBridgeMutation = useCreateBridge();
  const assignSitesMutation = useAssignSitesToRoute();

  const form = useForm<CreateBridgeFormValues>({
    resolver: zodResolver(createBridgeSchema),
    defaultValues: {
      name: '',
      initialRouteCidr: '0.0.0.0/0',
    },
  });

  const initialRouteCidr = useWatch({ control: form.control, name: 'initialRouteCidr' });

  const onSubmit = async (data: CreateBridgeFormValues) => {
    setIsCreating(true);
    try {
      const connectionType =
        selectedType === 'cloudflared'
          ? ConnectionType.Cloudflared
          : ConnectionType.CloudflareIpsec;

      // Create the bridge with the initial route CIDR
      // The backend creates a default route automatically with this CIDR
      const bridge = await createBridgeMutation.mutateAsync({
        name: data.name,
        type: connectionType,
        initialRouteCidr: data.initialRouteCidr || undefined,
      });

      // If sites are selected, assign them to the default route
      // The bridge response includes the routes array with the default route
      if (selectedSites.length > 0 && bridge.routes?.length > 0) {
        const defaultRouteId = bridge.routes[0].id;
        await assignSitesMutation.mutateAsync({
          siteIds: selectedSites,
          routeId: defaultRouteId,
        });
      }

      // Navigate to the bridge detail page
      navigate(`/edgelink/${bridge.id}`);
    } finally {
      setIsCreating(false);
    }
  };

  const isPending = isCreating || createBridgeMutation.isPending || assignSitesMutation.isPending;

  const bridgeTypes = [
    {
      id: 'cloudflared' as const,
      label: 'Cloudflare Tunnel',
      icon: Cloud,
      description:
        'Fast, secure tunnel via Cloudflare\'s global edge network. Recommended for most deployments.',
      available: true,
    },
    {
      id: 'ipsec' as const,
      label: 'IPSec VPN',
      icon: Shield,
      description: 'Traditional site-to-site VPN connection for enterprise networks.',
      available: false,
      disabledReason: 'Coming Soon',
    },
  ];

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/edgelink"
          className={cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'mb-4 -ml-2')}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to EdgeLink
        </Link>
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-lg border border-muted/40 bg-muted/10">
            <Network className="h-6 w-6 text-lime" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Add EdgeLink Bridge</h1>
            <p className="text-muted-foreground">
              Deploy a new EdgeLink bridge to establish a secure connection to your infrastructure
            </p>
          </div>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          {/* Bridge Type Selection */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">Connection Type</Label>
            <div className="grid grid-cols-2 gap-4">
              {bridgeTypes.map((type) => {
                const Icon = type.icon;
                const isSelected = selectedType === type.id;
                const isDisabled = !type.available;

                return (
                  <button
                    key={type.id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => !isDisabled && setSelectedType(type.id)}
                    className={cn(
                      'relative flex flex-col rounded-lg border p-5 text-left transition-all',
                      isSelected && type.available
                        ? 'border-lime bg-lime/10'
                        : 'border-gray-700 hover:border-gray-600',
                      isDisabled && 'cursor-not-allowed opacity-60'
                    )}
                  >
                    {isDisabled && type.disabledReason && (
                      <div className="absolute top-3 right-3">
                        <span className="inline-flex items-center gap-1 rounded-full bg-yellow-500/10 px-2 py-0.5 text-[10px] font-semibold text-yellow-500 border border-yellow-500/30">
                          <Lock className="h-2.5 w-2.5" />
                          {type.disabledReason}
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="bridgeType"
                        checked={isSelected}
                        onChange={() => {}}
                        disabled={isDisabled}
                        className="sr-only"
                      />
                      <Icon
                        className={cn('h-6 w-6', isSelected ? 'text-lime' : 'text-muted-foreground')}
                      />
                      <span
                        className={cn(
                          'font-semibold text-lg',
                          isSelected ? 'text-lime' : 'text-white'
                        )}
                      >
                        {type.label}
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-gray-400">{type.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bridge Name */}
          <Card className="bg-card2">
            <CardHeader>
              <CardTitle className="text-lg">Bridge Details</CardTitle>
              <CardDescription>Configure your EdgeLink bridge settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Bridge Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="e.g., Auckland-Warehouse-01"
                        className="max-w-md"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>
                      A descriptive name to identify this bridge in your infrastructure
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Initial Route */}
          <Card className="bg-card2">
            <CardHeader>
              <CardTitle className="text-lg">Initial Route</CardTitle>
              <CardDescription>
                Optionally configure an initial network route for this bridge
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="initialRouteCidr"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      Network Range (CIDR){' '}
                      <span className="text-muted-foreground font-normal">(Optional)</span>
                    </FormLabel>
                    <FormControl>
                      <CidrInput
                        value={field.value || ''}
                        onChange={field.onChange}
                        showValidation={true}
                        placeholder="e.g., 192.168.1.0/24"
                        className="max-w-md"
                      />
                    </FormControl>
                    <FormDescription>
                      The local network subnet accessible through this bridge. You can add more routes after creation.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          {/* Site Assignment */}
          <Card className="bg-card2">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-lime" />
                <div>
                  <CardTitle className="text-lg">Assign Sites</CardTitle>
                  <CardDescription>
                    Select sites to connect through this EdgeLink bridge
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <SiteMultiSelect
                value={selectedSites}
                onChange={setSelectedSites}
                disabled={!initialRouteCidr}
              />
              {!initialRouteCidr && selectedSites.length === 0 && (
                <p className="text-xs text-yellow-500 mt-2">
                  Add an initial route above to enable site assignment
                </p>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex items-center justify-end gap-4 border-t pt-6">
            <Button type="button" variant="outline" onClick={() => navigate('/edgelink')} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating Bridge...
                </>
              ) : (
                <>
                  <Network className="h-4 w-4 mr-2" />
                  Create Bridge
                </>
              )}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
